import React from 'react';

import { Box } from '@mui/material';

import { CenteredCircularProgress } from 'components/Progress';

import { useSearch } from 'utils/hooks/search';

import { LinksType, SearchResponse, UuidableName } from 'utils/api/types';
import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';
import { findLinkByEntity } from 'utils/api/modules/tasks/tasks.utils';

import API from 'utils/api';

import { StyledAutocomplete, StyledTextField } from 'components/Input/styles';

import { InspectionEntity } from 'utils/api/modules/workflows/workflows.types';

import { StyledMenuItem, EditTaskRowName } from '../../EditTaskProperties/styles';

const loadingOption = { uuid: 'loading', name: 'Loading...' } as SearchResponse;
const nullOption = { uuid: 'null', name: '' } as SearchResponse;

const Row: React.FC<{ name: string; children: React.ReactNode; id?: string }> = ({
  name,
  children,
  id,
}): React.ReactElement => (
  <div key={id}>
    <EditTaskRowName>{name}</EditTaskRowName>
    <div>{children}</div>
  </div>
);

const InspectionField: React.FunctionComponent<{
  updateField: (field: string, value: LinksType | null) => void;
  taskDetailsData: TaskListResponse;
}> = ({ updateField, taskDetailsData }): React.ReactElement => {
  const {
    results: inspectionsList,
    loading: loadingInspections,
    searchQuery: searchQueryInspections,
  } = useSearch(API.workflows.searchInspections, undefined, { limit: 10 });

  const [selectedInspection, setSelectedInspection] = React.useState<SearchResponse | null | undefined>(undefined);

  const onChangeSearch = async (s: string): Promise<void> => {
    await searchQueryInspections(s);
  };

  React.useEffect(() => {
    const inspectionLink = findLinkByEntity(taskDetailsData.links, InspectionEntity);
    setSelectedInspection({
      name: inspectionLink?.instance.name || '',
      uuid: inspectionLink?.instance.uuid || '',
    });
  }, []);

  return (
    <Row name="Form" id="field-2">
      <StyledAutocomplete
        key={`${selectedInspection?.name}_${selectedInspection?.uuid}`}
        id="inspection-dropdown-field"
        options={inspectionsList}
        value={selectedInspection === undefined ? nullOption : selectedInspection}
        renderOption={(props, option: UuidableName) => {
          const { key, ...restProps } = props;
          return option.uuid === loadingOption.uuid ? (
            <CenteredCircularProgress key={key} />
          ) : (
            <Box sx={{ alignItems: 'center' }} key={key}>
              <StyledMenuItem
                {...restProps}
                value={option.uuid}
                onClick={() => {
                  setSelectedInspection({
                    name: option.name,
                    uuid: option.uuid,
                  });

                  updateField('links', {
                    entity: InspectionEntity,
                    instance: { name: option.name, uuid: option.uuid },
                  });
                }}
              >
                {' '}
                {option.name}{' '}
              </StyledMenuItem>
            </Box>
          );
        }}
        getOptionLabel={(option: SearchResponse) => option?.name as string}
        getOptionDisabled={(option: SearchResponse) =>
          option.uuid === loadingOption.uuid || option.uuid === selectedInspection?.uuid
        }
        renderInput={(params) => (
          <StyledTextField
            {...params}
            onChange={(e) => {
              onChangeSearch(e.target.value);
            }}
            InputProps={{
              ...params.InputProps,
              type: 'search',
            }}
            placeholder="Search"
          />
        )}
        loading={loadingInspections}
        fullWidth
        disableClearable={!selectedInspection?.uuid}
        onChange={() => {
          setSelectedInspection(undefined);
          searchQueryInspections('');
        }}
        onInputChange={(_, __, reason) => {
          if (reason === 'clear') {
            updateField('links', {
              entity: InspectionEntity,
              // @ts-ignore
              instance: null,
            });
          }
        }}
      />
    </Row>
  );
};

export default InspectionField;
